// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import {
  Building2, Save
} from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type { CrmMasterCompany as CompanyData } from '@easyflow/shared';
import {
  InputField, AddressField
} from './shared';

export function TabAzienda() {
  // Partial: il form parte vuoto e il BE può non avere ancora la riga azienda.
  const [company, setCompany] = useState<Partial<CompanyData>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.get(`${CRM_BASE}/company`).then((res) => {
      if (res.success && res.data) setCompany(res.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`${CRM_BASE}/company`, company);
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string) => (value: string) => setCompany((c) => ({ ...c, [field]: value }));

  if (loading) return <div className="text-muted-foreground dark:text-muted-foreground/70 p-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-foreground dark:text-white font-semibold mb-4 flex items-center gap-2"><Building2 className="h-5 w-5" /> Dati Societari</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InputField label="Ragione Sociale" value={company.company_name} onChange={update('company_name')} className="md:col-span-2" uppercase />
          <InputField label="Partita IVA" value={company.vat_number} onChange={update('vat_number')} />
          <InputField label="Codice Fiscale" value={company.fiscal_code} onChange={update('fiscal_code')} />
          <InputField label="Regime Fiscale" value={company.tax_regime} onChange={update('tax_regime')} />
          <InputField label="Codice ATECO" value={company.ateco_code} onChange={update('ateco_code')} />
          <InputField label="Numero REA" value={company.rea_number} onChange={update('rea_number')} />
          <InputField label="Capitale Sociale" value={company.share_capital} onChange={update('share_capital')} />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-foreground dark:text-white font-semibold mb-4">Sede Legale</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <AddressField value={company.legal_address ?? undefined} className="md:col-span-2" onSelect={(a) => setCompany((c) => ({ ...c, legal_address: a.address ?? '', ...(a.city !== undefined ? { legal_city: a.city } : {}), ...(a.province !== undefined ? { legal_province: a.province } : {}), ...(a.postal_code !== undefined ? { legal_zip: a.postal_code } : {}) }))} />
          <InputField label="Indirizzo" value={company.legal_address} onChange={update('legal_address')} className="md:col-span-2" />
          <InputField label="Città" value={company.legal_city} onChange={update('legal_city')} />
          <InputField label="Provincia" value={company.legal_province} onChange={update('legal_province')} />
          <InputField label="CAP" value={company.legal_zip} onChange={update('legal_zip')} />
          <InputField label="Paese" value={company.legal_country} onChange={update('legal_country')} />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-foreground dark:text-white font-semibold mb-4">Contatti</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField label="Telefono" value={company.phone} onChange={update('phone')} />
          <InputField label="Email" value={company.email} onChange={update('email')} type="email" />
          <InputField label="PEC" value={company.pec} onChange={update('pec')} type="email" />
          <InputField label="Sito Web" value={company.website} onChange={update('website')} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-foreground dark:text-white font-semibold mb-4">Dati Bancari</h3>
          <div className="space-y-4">
            <InputField label="IBAN" value={company.iban} onChange={update('iban')} />
            <InputField label="Nome Banca" value={company.bank_name} onChange={update('bank_name')} />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
            <h3 className="text-foreground dark:text-white font-semibold mb-4">Fatturazione Elettronica</h3>
            <InputField label="Codice SDI" value={company.sdi_code} onChange={update('sdi_code')} />
          </div>
          <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-6">
            <h3 className="text-foreground dark:text-white font-semibold mb-4">Rappresentante Legale</h3>
            <InputField label="Nome e Cognome" value={company.legal_representative} onChange={update('legal_representative')} />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-foreground dark:text-white px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50">
          <Save className="h-4 w-4" />
          {saving ? 'Salvataggio...' : 'Salva Dati Azienda'}
        </button>
      </div>
    </div>
  );
}

